import React, { useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Modal,
  Segment,
  Grid,
  Icon,
  Form,
  Button,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import Mappa from "../common/Mappa";
import InfoWeb from "../common/InfoWeb";
import PrezzoOfferta from "./PrezzoOfferta";
import useOffertaBooking from "./useOffertaBooking";

/**
 * Variante a modale di DettaglioOfferta.js, usata dal solo flusso "private_new": la scheda
 * dell'offerta è aperta come modale (mirror di OfferCourseDialog.tsx del nuovo portale) invece
 * che come pannello inline, ma la logica di stato/prezzo resta identica perché condivide lo
 * stesso useOffertaBooking.
 */
function DettaglioOffertaModal({
  open,
  onClose,
  corso,
  sessioniCorso,
  datiScuola,
  promozioniNew,
  appData,
  selectedDates,
  preferredNumAllievi,
  onAvantiClick,
}) {
  const {
    sessioni,
    idxSessione,
    setIdxSessione,
    sessioneSelezionata,
    giorniOfferta,
    numAllievi,
    setNumAllievi,
    numAllieviDisponibili,
    periodoOptions,
    listinoInUso,
    coordinateRitrovo,
    mapVisible,
    setMapVisible,
    buildAvantiArgs,
  } = useOffertaBooking({
    corso,
    sessioniCorso,
    datiScuola,
    promozioniNew,
    appData,
    selectedDates,
    preferredNumAllievi,
  });

  const { t, i18n } = useTranslation([datiScuola.customTranslationNamespace]);

  useEffect(() => {
    i18n.emit("languageChanged");
  }, [datiScuola.customTranslationNamespace]);

  const isLoadingSessioni = sessioniCorso == null;
  const hasNoDisponibilita = !isLoadingSessioni && !sessioneSelezionata;

  const infoWebTesti = (datiScuola.infoWeb ?? []).filter((x) => x.sezione === 0);
  const hasInfoWeb = infoWebTesti.length > 0;

  const avantiArgs = buildAvantiArgs();

  return (
    <Modal open={open} onClose={onClose} closeIcon size="large">
      <Modal.Header>
        <span dangerouslySetInnerHTML={{ __html: corso.titolo }}></span>
        {corso.sottoTitolo && (
          <div
            style={{ fontSize: "0.6em", fontWeight: "normal", color: "grey", marginTop: "0.3em" }}
            dangerouslySetInnerHTML={{ __html: corso.sottoTitolo }}
          ></div>
        )}
      </Modal.Header>

      <Modal.Content scrolling>
        {isLoadingSessioni && (
          <Segment basic textAlign="center">
            {t("caricamento_sessioni")}
          </Segment>
        )}

        {hasNoDisponibilita && (
          <Segment basic textAlign="center">
            {t("nessuna_disponibilita")}
          </Segment>
        )}

        {!isLoadingSessioni && sessioneSelezionata && (
          <>
            <Segment>
              <div>
                <h3>{t("opzioni_prenotazione")}</h3>
                <p style={{ color: "grey" }}>{t("opzioni_prenotazione_hint")}</p>
              </div>

              <Form>
                <Form.Group widths="equal">
                  {sessioni.length > 1 ? (
                    <Form.Dropdown
                      selection
                      label={t("periodo")}
                      options={periodoOptions}
                      value={idxSessione}
                      onChange={(e, data) => setIdxSessione(data.value)}
                    />
                  ) : (
                    <Form.Input
                      label={t("periodo")}
                      value={`${moment(sessioneSelezionata.dal).format(
                        "DD/MM/YYYY"
                      )} → ${moment(sessioneSelezionata.al).format("DD/MM/YYYY")}`}
                      readOnly
                      fluid
                      onChange={() => {}}
                    />
                  )}

                  <Form.Dropdown
                    selection
                    label={t("numero_persone")}
                    options={numAllieviDisponibili}
                    value={numAllievi}
                    onChange={(e, data) => setNumAllievi(data.value)}
                  />
                </Form.Group>

                {/* Il nuovo portale non mostra il ritrovo in questa modale: qui viene mantenuto
                    perché rimuoverlo farebbe perdere un'informazione che il cliente legacy ha
                    sempre avuto (deviazione intenzionale e approvata). */}
                <div style={{ marginTop: "1em" }}>
                  <label>
                    <strong>{t("ritrovo")}</strong>
                  </label>
                </div>
                <div>
                  <span>{sessioneSelezionata.ritrovo}</span>
                  {coordinateRitrovo && (
                    <span style={{ marginLeft: "0.5em" }}>
                      <Icon
                        name="map pin"
                        circular
                        link
                        color="red"
                        onClick={() => setMapVisible(!mapVisible)}
                      ></Icon>
                    </span>
                  )}
                </div>
                {mapVisible && coordinateRitrovo && (
                  <Mappa
                    ritrovo={t("ritrovo")}
                    lat={coordinateRitrovo.lat}
                    lng={coordinateRitrovo.lng}
                    zoom={coordinateRitrovo.z}
                  ></Mappa>
                )}
              </Form>
            </Segment>

            <Segment>
              <h3>{t("riepilogo_selezione")}</h3>
              <Grid stackable columns="equal">
                {hasInfoWeb && (
                  <Grid.Column>
                    <InfoWeb testi={infoWebTesti} sezione={0} />
                  </Grid.Column>
                )}
                <Grid.Column textAlign={hasInfoWeb ? "right" : "left"}>
                  <p>
                    <strong>{t("giornate_di_corso")}</strong>
                  </p>
                  <p>{t("numero_giornate", { count: giorniOfferta })}</p>
                  <p>
                    {moment(sessioneSelezionata.dal).format("DD/MM/YYYY")} →{" "}
                    {moment(sessioneSelezionata.al).format("DD/MM/YYYY")}
                  </p>

                  {sessioneSelezionata.righe?.length > 0 && (
                    <>
                      <p>
                        <strong>{t("lezioni_selezionate")}</strong>
                      </p>
                      <ul>
                        {sessioneSelezionata.righe.map((riga, index) => (
                          <li key={`${riga.rigaStampa}-${index}`}>{riga.rigaStampa}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </Grid.Column>
              </Grid>
            </Segment>

            <PrezzoOfferta
              listinoInUso={listinoInUso}
              numAllievi={numAllievi}
              datiScuola={datiScuola}
            />
          </>
        )}
      </Modal.Content>

      {!isLoadingSessioni && sessioneSelezionata && (
        <Modal.Actions>
          <Button
            primary
            size="big"
            disabled={!avantiArgs}
            onClick={() => {
              if (!avantiArgs) return;
              onAvantiClick(...avantiArgs);
            }}
          >
            <Icon name="cart" />
            {t("procedi")}
          </Button>
        </Modal.Actions>
      )}
    </Modal>
  );
}

DettaglioOffertaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  corso: PropTypes.object.isRequired,
  sessioniCorso: PropTypes.object,
  datiScuola: PropTypes.object.isRequired,
  promozioniNew: PropTypes.object.isRequired,
  appData: PropTypes.object.isRequired,
  selectedDates: PropTypes.array,
  preferredNumAllievi: PropTypes.number,
  onAvantiClick: PropTypes.func.isRequired,
};

export default DettaglioOffertaModal;
